import { HttpService } from '@core/services/http.service.ts';
import { ApiMethod } from '@core/interfaces/api.ts';
import { apiURL } from '@utils/environment.ts';
import { AuditLogEntry, getStoredLogs, logAuditLocal, getPaginatedLogs } from '@utils/auditLog.ts';
import { PaginateResponse } from '@utils/appUtils.ts';

export interface AuditLogFilter {
  page?: number;
  size?: number;
  action?: string;
  username?: string;
  from?: string;
  to?: string;
}

/**
 * Service journal des actions.
 *
 * Stratégie "API-first with localStorage fallback" :
 *  - Si le backend expose GET/POST /audit-log → les appels s'y dirigent automatiquement.
 *  - Si l'API est indisponible (réseau, 404, 501…) → bascule silencieusement sur localStorage.
 *
 * Aucune modification nécessaire côté front quand le backend sera livré.
 */
export class AuditLogService extends HttpService {
  private readonly BASE_URL = `${apiURL}/audit-log`;

  /** Envoie une entrée au backend. Fallback localStorage si indisponible. */
  async create(entry: Omit<AuditLogEntry, 'id' | 'timestamp'> & { timestamp?: string }): Promise<void> {
    try {
      this.urlAPI = this.BASE_URL;
      await this.doPost<void, typeof entry>(entry);
      // Si le backend a accepté, on n'enregistre PAS en localStorage (évite les doublons)
    } catch {
      // Backend non disponible → fallback localStorage
      logAuditLocal(entry);
    }
  }

  /** Récupère les logs depuis le backend. Fallback localStorage si indisponible. */
  async findAll(filter: AuditLogFilter): Promise<PaginateResponse<AuditLogEntry>> {
    const { page = 0, size = 20, action, username, from, to } = filter;
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        ...(action ? { action } : {}),
        ...(username ? { username } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      this.urlAPI = `${this.BASE_URL}?${params}`;
      const result = await this.doGet<PaginateResponse<AuditLogEntry>>();
      return result;
    } catch {
      // Backend non disponible → fallback localStorage
      return getPaginatedLogs(page, size, { action, username, from, to });
    }
  }

  /** Vide le journal (backend + localStorage). */
  async clear(): Promise<void> {
    try {
      this.urlAPI = this.BASE_URL;
      await this.doDelete<void>();
    } catch {
      // Si le backend échoue, on vide quand même le localStorage
    }
    localStorage.removeItem('admin_audit_logs');
  }

  /** Vérifie si le backend audit est disponible (pour affichage informatif). */
  async isBackendAvailable(): Promise<boolean> {
    try {
      this.urlAPI = `${this.BASE_URL}?page=0&size=1`;
      await this.doGet<unknown>();
      return true;
    } catch {
      return false;
    }
  }
}

export const auditLogService = new AuditLogService();
